import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchActiveCarePlansService,
  activeCarePlanDetailViewService,
  fetchDraftCarePlansService,
  fetchSections,
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
  updateSectionItemsOrder,
  checkCarePlanExistsService,
  createCarePlanService,
  fetchDefaultContactsService,
  createDefaultContactService,
  updateDefaultContactService,
  deleteDefaultContactService,
  checkOrCreateCarePlanService,
  saveCarePlanService,
  getCarePlanInstance,
  getDataForViewService,
  addMdtUserToCarePlanService,
  deleteCarePlanService,
  addUsersToCarePlanService,
  removeUserFromCarePlanService,
  createDraftCarePlanService,
  publishCarePlanService,
  lockCarePlanService,
  unlockCarePlanService,
  updateSectionService,
  sendFeedbackService,
  fetchFeedbacksService,
  fetchCarePlanAuditLogsService,
} from "../../services/dashboardServices";
import {
  loadCarePlanItems,
  loadCarePlanSections,
  invalidatePagesAfter, // invalidate pages after all the pages next to deleted care plan page to show the care plan listing correctly in draft and active pages
  setDraftCurrentPage,
  setActiveCurrentPage,
} from "../slices/careplanSlice";

// Create thunk to fetch paginated active care plans data from API
export const fetchActiveCarePlanData = createAsyncThunk(
  "careplan/fetchActiveCarePlanData",
  async (
    { page, rowsPerPage, searchQuery, forceRefresh = false, ordering = "" },
    { getState }
  ) => {
    const state = getState();
    const {
      activeCarePlans,
      activeSearchQuery,
      activeRowsPerPage,
      activeTotalRows,
    } = state.careplan;

    // Determine if we need to refresh due to changed parameters
    const needsRefreshDueToParamsChange =
      activeRowsPerPage !== rowsPerPage ||
      activeSearchQuery.nhsNumber !== (searchQuery?.nhsNumber || "") ||
      activeSearchQuery.createdBy !== (searchQuery?.createdBy || "");

    // Check if data for this page is already in state and can be reused
    if (
      activeCarePlans[page] &&
      !forceRefresh &&
      !ordering &&
      !needsRefreshDueToParamsChange
    ) {
      return {
        data: activeCarePlans[page],
        totalRows: activeTotalRows,
        page,
        rowsPerPage,
        searchQuery,
      };
    }
    // Fetch data from the API
    const response = await fetchActiveCarePlansService(
      page,
      rowsPerPage,
      searchQuery,
      ordering
    );

    // Extract data from the response
    return {
      data: response.results,
      totalRows: response.count,
      page,
      rowsPerPage,
      searchQuery,
    };
  }
);

export const activeCarePlanDetailView = createAsyncThunk(
  "activeCarePlan/detailView",
  async (carePlanId, { rejectWithValue }) => {
    try {
      const response = await activeCarePlanDetailViewService(carePlanId);
      return response; // Updated care plan data
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error incrementing view count"
      );
    }
  }
);

// Create thunk to fetch paginated draft care plans data from API
export const fetchDraftCarePlanData = createAsyncThunk(
  "careplan/fetchDraftCarePlanData",
  async (
    { page, rowsPerPage, searchQuery, forceRefresh = false, ordering = "" },
    { getState }
  ) => {
    const state = getState();
    const {
      draftCarePlans,
      draftSearchQuery,
      draftRowsPerPage,
      draftTotalRows,
    } = state.careplan;

    // Determine if we need to refresh due to changed parameters
    const needsRefreshDueToParamsChange =
      draftRowsPerPage !== rowsPerPage ||
      draftSearchQuery.nhsNumber !== (searchQuery?.nhsNumber || "") ||
      draftSearchQuery.createdBy !== (searchQuery?.createdBy || "");

    // Check if the data for the current page already exists in the state
    if (
      draftCarePlans[page] &&
      !forceRefresh &&
      !ordering &&
      !needsRefreshDueToParamsChange
    ) {
      return {
        data: draftCarePlans[page],
        totalRows: draftTotalRows,
        page,
        rowsPerPage,
        searchQuery,
      };
    }
    // Fetch data from the API
    const response = await fetchDraftCarePlansService(
      page,
      rowsPerPage,
      searchQuery,
      ordering
    );

    // Extract data from the response
    return {
      data: response.results,
      totalRows: response.count,
      page,
      rowsPerPage,
      searchQuery,
    };
  }
);

// Create thunk to fetch sections
export const fetchSectionsData = createAsyncThunk(
  "careplan/fetchSections",
  async ({ forceRefresh = false } = {}, { getState, dispatch }) => {
    const state = getState();
    if (state.careplan.sectionsLoaded && !forceRefresh) {
      return state.careplan.sections; // Return cached sections if already loaded
    }
    const response = await fetchSections();
    dispatch(loadCarePlanSections(response));
    return response; // Assuming response contains the list of sections
  }
);

// Redux Action for updating items in a section
export const updateSectionItemsOrderAction = createAsyncThunk(
  "careplan/updateSectionItemsOrder",
  async ({ sectionId, sectionData }, { dispatch, rejectWithValue }) => {
    try {
      await updateSectionItemsOrder(sectionId, sectionData);
      dispatch(fetchSectionsData({ forceRefresh: true }));
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSectionAction = createAsyncThunk(
  "careplan/updateSection",
  async ({ sectionId, resource_link, qr_description }, { rejectWithValue }) => {
    try {
      const response = await updateSectionService(sectionId, {
        resource_link,
        qr_description,
      });
      return response; // This is the updated section data from the server
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Thunk to create items
export const createItemAction = createAsyncThunk(
  "careplan/createItem",
  async (itemData, { dispatch, rejectWithValue }) => {
    try {
      await createItem(itemData);
      // Re-fetch items after successfully creating one
      dispatch(fetchItemsData({ forceRefresh: true }));
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Thunk to update items
export const updateItemAction = createAsyncThunk(
  "careplan/updateItem",
  async ({ id, formData }, { dispatch, rejectWithValue }) => {
    try {
      await updateItem(id, formData);
      // Re-fetch items after successfully creating one
      dispatch(fetchItemsData({ forceRefresh: true }));
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Thunk to create items
export const deleteItemAction = createAsyncThunk(
  "careplan/deleteItem",
  async (itemId, { dispatch, rejectWithValue }) => {
    try {
      await deleteItem(itemId);
      // Re-fetch items after successfully deleting one
      dispatch(fetchItemsData({ forceRefresh: true }));
      // After deleting the item, section need to be updated to remove the deleted item id from its items array
      dispatch(fetchSectionsData({ forceRefresh: true }));
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Thunk to fetch items
export const fetchItemsData = createAsyncThunk(
  "careplan/fetchItems",
  async ({ forceRefresh = false } = {}, { getState, dispatch }) => {
    const state = getState();
    if (state.careplan.itemsLoaded && !forceRefresh) {
      return state.careplan.items; // Return cached items if already loaded
    }
    const response = await fetchItems();
    dispatch(loadCarePlanItems(response));
    return response; // response contains the list of items
  }
);

// Action to check a care plan with nhs number exists
export const checkCarePlanExists = createAsyncThunk(
  "careplan/checkCarePlanExists",
  async (nhsNumber) => {
    try {
      const response = await checkCarePlanExistsService(nhsNumber);
      return response; // { exists: true/false }
    } catch (error) {
      throw error;
    }
  }
);

// Action to create care plan
export const createCarePlan = createAsyncThunk(
  "careplan/createCarePlan",
  async (_, { getState, dispatch }) => {
    const state = getState();
    const carePlanData = state.careplan.carePlan;

    // Prepare data to send
    const serializedCarePlanData = {
      nhsNumber: carePlanData.nhsNumber,
      important_info: carePlanData.important_info || "",
      sections: carePlanData.sections.map((section) => ({
        id: section.id,
        items: section.items.map((item) => ({
          id: item.id,
          answer: item.answer || "",
          ...(String(item.id).startsWith("new-") && {
            title: item.title,
            description: item.description,
          }),
          ...(item.fromOverlay && { is_overlay_selected: true }),
        })),
      })),
      contacts: carePlanData.contacts,
    };

    const response = await createCarePlanService(serializedCarePlanData);

    // After creation, refetch page 1 of draft care plans
    await dispatch(
      fetchDraftCarePlanData({
        page: 1,
        rowsPerPage: state.careplan.draftRowsPerPage,
        searchQuery: state.careplan.draftSearchQuery,
        forceRefresh: true,
      })
    );

    // Invalidate pages after page 1
    dispatch(invalidatePagesAfter({ type: "draft", fromPage: 1 }));

    return response;
  }
);

export const fetchDefaultContacts = createAsyncThunk(
  "careplan/fetchDefaultContacts",
  async (_, { getState }) => {
    const { defaultContactsLoaded, carePlan } = getState().careplan;
    if (defaultContactsLoaded) return carePlan.contacts;
    const response = await fetchDefaultContactsService();
    return response;
  }
);

export const createDefaultContact = createAsyncThunk(
  "careplan/createDefaultContact",
  async (newContact) => {
    return await createDefaultContactService(newContact);
  }
);

export const updateDefaultContact = createAsyncThunk(
  "careplan/updateDefaultContact",
  async ({ id, data }) => {
    const updated = await updateDefaultContactService(id, data);
    return updated;
  }
);

export const deleteDefaultContact = createAsyncThunk(
  "careplan/deleteDefaultContact",
  async (id) => {
    await deleteDefaultContactService(id);
    return id;
  }
);

// Action to check or create care plan
export const checkOrCreateCarePlan = createAsyncThunk(
  "careplan/checkOrCreateCarePlan",
  async (nhsNumber) => {
    try {
      const response = await checkOrCreateCarePlanService(nhsNumber);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Action to save care plan data
export const saveCarePlanData = createAsyncThunk(
  "careplan/saveCarePlanData",
  // async (publish = false, { getState, dispatch }) => {
  async ({ publish = false, isEdit = false } = {}, { getState, dispatch }) => {
    const state = getState();
    const carePlanData = { ...state.careplan.carePlan };
    const urgentCareContacts = carePlanData.contacts || [];

    if (publish) {
      carePlanData.is_published = true;
    }

    // Filter out the default contacts (those marked with `is_default`)
    const userDefinedContacts = urgentCareContacts.filter(
      (contact) => !contact.is_default
    );

    // Prepare the data in the format expected by the backend
    const serializedCarePlanData = {
      id: carePlanData.id,
      nhsNumber: carePlanData.nhsNumber,
      is_published: carePlanData.is_published,
      important_info: carePlanData.important_info || "", // Include important_info field
      sections: carePlanData.sections.map((section) => ({
        id: section.id,
        items: section.items.map((item) => {
          // Exclude items that have been removed
          return {
            id: item.id,
            answer: item.answer || "",
            order: item.order || 0,
            // Include title and description for new items
            ...(String(item.id).startsWith("new-") && {
              title: item.title,
              description: item.description,
            }),
            ...(item.fromOverlay && { is_overlay_selected: true }),
          };
        }),
      })),
      // Add the contacts here
      contacts: userDefinedContacts.map((contact) => ({
        name: contact.name,
        number: contact.number,
      })),
    };

    try {
      // const response = await saveCarePlanService(serializedCarePlanData);
      const response = await saveCarePlanService(
        serializedCarePlanData,
        isEdit
      );
      // Dispatch the action to fetch updated draft care plans after saving
      dispatch(fetchDraftCarePlanData({ page: 1, rowsPerPage: 12 }));
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const fetchCarePlanInstance = createAsyncThunk(
  "careplan/fetchCarePlanInstance",
  async (carePlanId, { dispatch }) => {
    const response = await getCarePlanInstance(carePlanId);
    // dispatch(loadCarePlanInstance(response));
    return response; // This will be used to populate carePlan
  }
);

export const getDataForView = createAsyncThunk(
  "careplan/getDataForView",
  async (publicId) => {
    const response = await getDataForViewService(publicId);
    // dispatch(loadCarePlanInstance(response));
    return response; // This will be used to populate carePlan
  }
);

// Action to add MDT users to a care plan
export const addMdtUserToCarePlan = createAsyncThunk(
  "careplan/addMdtUserToCarePlan",
  async (selectedMdtUsers, { getState }) => {
    const state = getState();
    const carePlanId = state.careplan.carePlan.id;

    const data = await addMdtUserToCarePlanService(
      carePlanId,
      selectedMdtUsers
    ); // Call service function
    return data;
  }
);

// Action to delete care plan
export const deleteCarePlan = createAsyncThunk(
  "careplan/deleteCarePlan",
  async ({ carePlanId, type }, { dispatch, getState }) => {
    await deleteCarePlanService(carePlanId);

    const preRefetchState = getState().careplan;

    let currentPage,
      rowsPerPage,
      searchQuery,
      currentPageData,
      newPageData,
      pageToFetch;

    if (type === "draft") {
      currentPage = preRefetchState.draftCurrentPage;
      rowsPerPage = preRefetchState.draftRowsPerPage;
      searchQuery = preRefetchState.draftSearchQuery;
      currentPageData = preRefetchState.draftCarePlans[currentPage] || [];
      newPageData = currentPageData.filter((plan) => plan.id !== carePlanId);

      // Determine the new page to fetch
      pageToFetch = currentPage;
      if (newPageData.length === 0 && currentPage > 1) {
        pageToFetch = currentPage - 1;
        dispatch(setDraftCurrentPage(pageToFetch));
      }

      // Fetch the determined page
      await dispatch(
        fetchDraftCarePlanData({
          page: pageToFetch,
          rowsPerPage,
          searchQuery,
          forceRefresh: true,
        })
      );

      // Invalidate subsequent pages
      dispatch(
        invalidatePagesAfter({
          type: "draft",
          fromPage: pageToFetch,
        })
      );
    } else if (type === "active") {
      currentPage = preRefetchState.activeCurrentPage;
      rowsPerPage = preRefetchState.activeRowsPerPage;
      searchQuery = preRefetchState.activeSearchQuery;
      currentPageData = preRefetchState.activeCarePlans[currentPage] || [];
      newPageData = currentPageData.filter((plan) => plan.id !== carePlanId);

      // Determine the new page to fetch
      pageToFetch = currentPage;
      if (newPageData.length === 0 && currentPage > 1) {
        pageToFetch = currentPage - 1;
        dispatch(setActiveCurrentPage(pageToFetch));
      }

      // Fetch the determined page
      await dispatch(
        fetchActiveCarePlanData({
          page: pageToFetch,
          rowsPerPage,
          searchQuery,
          forceRefresh: true,
        })
      );

      // Invalidate subsequent pages
      dispatch(
        invalidatePagesAfter({
          type: "active",
          fromPage: pageToFetch,
        })
      );
    }

    return { carePlanId, type };
  }
);

export const addUsersToCarePlan = createAsyncThunk(
  "careplan/addUsersToCarePlan",
  async ({ carePlanId, userAssignments }, { rejectWithValue }) => {
    // userAssignments is an array of { user_id, can_edit }
    try {
      const response = await addUsersToCarePlanService(
        carePlanId,
        userAssignments
      );
      return response; // Contains the updated care plan or new user assignments
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error adding users to care plan"
      );
    }
  }
);

export const removeUserFromCarePlan = createAsyncThunk(
  "careplan/removeUserFromCarePlan",
  async ({ carePlanId, userId }, { rejectWithValue }) => {
    try {
      const response = await removeUserFromCarePlanService(carePlanId, userId);
      return response; // Updated care plan data
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Thunk action to create a draft care plan
export const createDraftCarePlan = createAsyncThunk(
  "careplan/createDraftCarePlan",
  async (carePlanId, { dispatch, getState }) => {
    const response = await createDraftCarePlanService(carePlanId);

    const state = getState().careplan;

    // Refetch draft care plans to include the newly created draft
    await dispatch(
      fetchDraftCarePlanData({
        page: state.draftCurrentPage,
        rowsPerPage: state.draftRowsPerPage,
        searchQuery: state.draftSearchQuery,
        forceRefresh: true,
      })
    );

    // Invalidate draft pages beyond the current page
    dispatch(
      invalidatePagesAfter({ type: "draft", fromPage: state.draftCurrentPage })
    );

    return response;
    // try {
    //   // Call your API endpoint to create a draft
    //   // Assuming the endpoint is POST /careplans/{id}/draft
    //   // and it returns the newly created draft care plan data.
    //   const response = await createDraftCarePlanService(carePlanId);
    //   return response;
    // } catch (error) {
    //   return rejectWithValue(error.response?.data || "Failed to create draft");
    // }
  }
);

// Thunk action to publish a draft care plan
export const publishCarePlan = createAsyncThunk(
  "careplan/publishCarePlan",
  async ({ carePlanId, serviceUserName }, { dispatch, getState }) => {
    const response = await publishCarePlanService(carePlanId, serviceUserName);

    const state = getState().careplan;

    // Refetch draft care plans to remove the published one from draft list
    await dispatch(
      fetchDraftCarePlanData({
        page: state.draftCurrentPage,
        rowsPerPage: state.draftRowsPerPage,
        searchQuery: state.draftSearchQuery,
        forceRefresh: true,
      })
    );

    // Invalidate draft pages beyond the current page
    dispatch(
      invalidatePagesAfter({ type: "draft", fromPage: state.draftCurrentPage })
    );

    // Refetch active care plans to have the published in the list
    await dispatch(
      fetchActiveCarePlanData({
        page: 1,
        rowsPerPage: state.activeRowsPerPage,
        searchQuery: state.activeSearchQuery,
        forceRefresh: true,
      })
    );

    // Invalidate pages beyond page 1 for active
    dispatch(invalidatePagesAfter({ type: "active", fromPage: 1 }));

    return response;
  }
);

// Thunk action to lock a care plan
export const lockCarePlan = createAsyncThunk(
  "careplan/lockCarePlan",
  async (carePlanId, { rejectWithValue, dispatch }) => {
    try {
      const response = await lockCarePlanService(carePlanId);

      // Dispatch an action to update the store properly
      dispatch({
        type: "careplan/updateCarePlanLockStatus",
        payload: {
          id: carePlanId,
          is_locked: true,
          locked_by: response.locked_by, // Use response directly (not response.data)
        },
      });

      return response;
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue(error.response.data);
      }
      throw error;
    }
  }
);

// Thunk action to unlock a care plan
export const unlockCarePlan = createAsyncThunk(
  "careplan/unlockCarePlan",
  async (carePlanId) => {
    const response = await unlockCarePlanService(carePlanId);
    return response;
  }
);

// Thunk action to fetch draft care plans for the overview page
export const fetchOverviewDraftCarePlans = createAsyncThunk(
  "careplan/fetchOverviewDraftCarePlans",
  async () => {
    const response = await fetchDraftCarePlansService(1, 5, {});
    return { data: response.results }; // Ensure the payload is in the correct format
  }
);

export const submitFeedback = createAsyncThunk(
  "careplan/submitFeedback",
  async ({ publicId, form }, { rejectWithValue }) => {
    try {
      const { data } = await sendFeedbackService(publicId, form);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const loadFeedbacks = createAsyncThunk(
  "feedback/loadAll",
  async (publicId, { rejectWithValue }) => {
    try {
      return await fetchFeedbacksService(publicId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchCarePlanAuditLogs = createAsyncThunk(
  "carePlanLogs/load",
  async ({ carePlanId, isDraft }) => {
    const logs = await fetchCarePlanAuditLogsService(carePlanId, isDraft);
    return { carePlanId, logs };
  }
);

export const loadCarePlan = (carePlan) => ({
  type: "LOAD_CARE_PLAN",
  payload: carePlan,
});

export const updateAnswer = (sectionId, itemId, answer) => ({
  type: "UPDATE_ANSWER",
  payload: { sectionId, itemId, answer },
});

export const setCurrentSectionIndex = (index) => ({
  type: "SET_CURRENT_SECTION_INDEX",
  payload: index,
});
